import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Card} from 'shells/card/card';
import {FormField} from 'shells/form/form-field';
import {Link} from 'shells/link/link';
import type {ListOptionWithIcon} from 'shells/select-option-components/option-with-icon';
import {COMPANY_MERGE_FIELD_OPTIONS, RECIPIENT_MERGE_FIELD_OPTIONS} from 'shells/textfield/consts';
import {TextField} from 'shells/textfield/textfield';
import {TextfieldWithCharacterLimit} from 'shells/textfield/textfield-with-character-limit';
import {Body} from 'shells/typography/body';

import type {
    SmsBroadcastFragment as Broadcast,
    SmsEditionUpdateInput,
    SmsEditionFragment_audience_fromSavedFilter as SavedFilter,
} from 'nutshell-graphql-types';

import {GraphQLSelectUsers} from '../../../core/select/graphql-select-users';

import {appHistory} from '../../../../history';

import {SendToSavedListField} from '../../editions/editions-details/send-to-saved-list-field';
import {ToField} from '../../editions/editions-details/to-field';
import {useCampaignSetOwner} from '../../editions/graphql/hooks/use-campaign-set-owner';

import {MAX_CHARACTERS, MIN_CHARACTERS} from '../consts';
import {OptOutTextSelect} from '../opt-out-text-select';
import type {OptOutOption} from '../opt-out-text-select';
import {SentFromField} from '../SentFromField';

const MERGE_FIELD_OPTIONS = [...RECIPIENT_MERGE_FIELD_OPTIONS, ...COMPANY_MERGE_FIELD_OPTIONS];

type Props = {
    broadcast: Broadcast;
    prepareToSendPath: string;
    content: string;
    onContentChange: (content: string) => void;
    onIsSavingDetailsChange: (isSavingDetails: boolean) => void;
    onUpdate: (input: SmsEditionUpdateInput) => Promise<any>;
};

export function BroadcastDetailsForm(props: Props) {
    const {broadcast} = props;

    const [optOut, setOptOut] = React.useState<string | null | undefined>(
        broadcast.edition.optOutText
    );
    const [ownerId, setOwnerId] = React.useState<string | null | undefined>(
        broadcast.owner ? broadcast.owner.id : null
    );
    const {setOwner} = useCampaignSetOwner();

    const recipientsList = broadcast.edition.audience && broadcast.edition.audience.fromSavedFilter;

    const readOnlyStatuses = ['SENDING', 'SENT', 'HALTED', 'IN_REVIEW', 'SCHEDULED'];
    const isReadOnly = Boolean(
        readOnlyStatuses.indexOf(broadcast.edition.status) !== -1 || broadcast.edition.deletedTime
    );

    // shouldShowNewAudienceManagement and sendToComponent capture three possible states:
    // Showing the new audience management field, the old one, or no field at all.
    const shouldShowNewAudienceManagement =
        NutClientConfig.hasNewAudienceManagementNUT14721 && !isReadOnly;

    const handleOwnerChange = React.useCallback(
        (selectedUser: ListOptionWithIcon) => {
            props.onIsSavingDetailsChange(true);
            setOwnerId(selectedUser.value);

            setOwner({
                campaignId: broadcast.id,
                userId: selectedUser.value,
            }).then(() => {
                props.onIsSavingDetailsChange(false);
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [broadcast, setOwner]
    );

    const handleClear = React.useCallback(() => {
        props.onIsSavingDetailsChange(true);
        setOwnerId(null);

        setOwner({
            campaignId: broadcast.id,
            userId: null,
        }).then(() => {
            props.onIsSavingDetailsChange(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [broadcast, setOwner]);

    const handleOptOutTextChange = (optOut: OptOutOption) => {
        setOptOut(optOut.label);

        props.onUpdate({
            editionId: broadcast.edition.id,
            optOutText: optOut.value,
        });
    };

    return (
        <Card hasBorder={true}>
            <form className='pad-16 flex flex-dir-col overflow-auto full-height border-box'>
                <FormField innerId='owner' label='Owner'>
                    <GraphQLSelectUsers
                        onChange={handleOwnerChange}
                        onClearValue={handleClear}
                        selectedId={ownerId}
                        isMulti={false}
                        noResultsText='No users were found'
                    />
                </FormField>
                <FormField innerId='from' label='From'>
                    <SentFromField from={broadcast.edition.from} />
                </FormField>
                {getToField(recipientsList)}
                <FormField innerId='content' label='Text message'>
                    {isReadOnly ? (
                        <TextField
                            multiline={true}
                            rows={4}
                            value={props.content}
                            // @ts-expect-error
                            hasNoSideBorders={false}
                            disabled={true}
                        />
                    ) : (
                        <TextfieldWithCharacterLimit
                            multiline={true}
                            rows={4}
                            placeholder='Type your message...'
                            maximumCharacters={MAX_CHARACTERS}
                            minimumCharacters={MIN_CHARACTERS}
                            value={props.content}
                            mergeFieldOptions={MERGE_FIELD_OPTIONS}
                            hasNoSideBorders={false}
                            onChange={props.onContentChange}
                            focusOnMount={true}
                        />
                    )}
                </FormField>
                <OptOutTextSelect
                    isRequired={true}
                    isDisabled={isReadOnly}
                    selectedOptOutText={optOut}
                    onOptOutTextChange={handleOptOutTextChange}
                />
            </form>
        </Card>
    );

    function getToField(recipientsList?: SavedFilter | null) {
        if (recipientsList) {
            return <SendToSavedListField savedFilter={recipientsList} />;
        }

        if (NutClientConfig.hasSmsAudiencesFF) {
            return (
                <ToField
                    edition={broadcast.edition}
                    isTemplate={false}
                    showPreviewMode={isReadOnly}
                    shouldShowNewAudienceManagement={shouldShowNewAudienceManagement}
                    setIsPreparingToSend={() => {}}
                    onEditionSave={props.onUpdate}
                    onSetSelectedEditorTab={() => {
                        appHistory.push(props.prepareToSendPath);
                    }}
                />
            );
        }

        return (
            <FormField innerId='to' label='To'>
                <Body isItalic={true} size='small'>
                    You’ll choose your recipients when you{' '}
                    {!broadcast.edition.deletedTime ? (
                        <Link
                            // @ts-expect-error
                            as={RoutedLink}
                            to={props.prepareToSendPath}
                            variant='primary'
                            size='small'
                        >
                            prepare to send
                        </Link>
                    ) : (
                        'prepare to send'
                    )}
                    .
                </Body>
            </FormField>
        );
    }
}
